import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { toast } from "sonner";
import { store } from "../Store";

export const SOURCE_ID = "branch_locator";

const TIME_ZONE = process.env.REACT_APP_TIME_ZONE ?? "UTC";
const ACCEPT_LANGUAGE = process.env.REACT_APP_ACCEPT_LANGUAGE ?? "ru";

function setLocaleHeaders(headers: Headers): void {
  headers.set("Accept-Language", ACCEPT_LANGUAGE);
  headers.set("X-Time-Zone", TIME_ZONE);
}

const fetchRejectNonOk: typeof fetch = async (input, init) => {
  const response = await fetch(input, init);
  if (!response.ok) {
    throw new Error();
  }
  return response;
};

function getRtkErrorMessage(error: FetchBaseQueryError): string {
  const data = error.data;
  if (data && typeof data === "object") {
    const rec = data as Record<string, unknown>;
    if (typeof rec.message === "string") {
      return rec.message;
    }
    if (typeof rec.error === "string") {
      return rec.error;
    }
  }
  if (typeof error.status === "string" && error.error) {
    return error.error;
  }
  return "Произошла ошибка";
}

const withGlobalErrorToast = (
  baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  return async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions);
    if (result.error) {
      toast.error(getRtkErrorMessage(result.error));
    }
    return result;
  };
};

const geocoderRawBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_GEOCODER_API_BASE_URL,
  fetchFn: fetchRejectNonOk,
  prepareHeaders: (headers, { getState }) => {
    const {
      auth: { token },
    } = (getState as typeof store.getState)();

    setLocaleHeaders(headers);
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("Accept", "application/json");
    }

    return headers;
  },
});

export const geocoderApiQuery = withGlobalErrorToast(geocoderRawBaseQuery);

const branchServiceRawBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BRANCH_API_BASE_URL,
  fetchFn: fetchRejectNonOk,
  prepareHeaders: (headers) => {
    setLocaleHeaders(headers);
    headers.set(
      "Authorization",
      `Bearer ${process.env.REACT_APP_BRANCH_API_CREDENTIAL}`,
    );
    headers.set("Accept", "application/json");
    return headers;
  },
});

export const branchApiQuery = withGlobalErrorToast(branchServiceRawBaseQuery);

const branchUserAuthRawBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BRANCH_API_BASE_URL,
  fetchFn: fetchRejectNonOk,
  prepareHeaders: (headers, { getState }) => {
    const {
      auth: { token },
    } = (getState as typeof store.getState)();
    setLocaleHeaders(headers);
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    headers.set("Accept", "application/json");
    return headers;
  },
});

export const branchApiUserAuthQuery = withGlobalErrorToast(
  branchUserAuthRawBaseQuery,
);

export interface Param {
  name: string;
  value: any;
}

export const getUrl = (url: string, params: any) => {
  const data = Object.entries(params).map(([name, value]) => ({ name, value }));
  return data?.filter((item) => !!item.value)?.length > 0
    ? joinToString(data, "&", url + "?", "", (item) =>
        item.value ? `${item.name}=${item.value}` : "",
      )
    : url;
};

export function joinToString<T>(
  array: T[],
  separator = ",",
  prefix = "",
  suffix = "",
  transform: (item: T) => string,
) {
  let res = prefix;
  for (let i = 0; i < array.length; i++) {
    if (i !== 0 && transform(array[i]) !== "") {
      res += separator;
    }
    res += transform(array[i]);
  }
  res += suffix;
  return res;
}
